import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Edit, Goal } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AGE_GROUPS = ['Junior League', 'Champions League', 'World Cup', 'Icons League'];
const STAGES_BY_LEAGUE = {
  'Junior League': ['Group Stage', 'Finals'],
  'World Cup': ['Group Stage', 'Finals'],
  'Champions League': ['Group Stage', 'Semi Finals', 'Finals'],
  'Icons League': ['Group Stage', 'Semi Finals', 'Playoff', 'Finals'],
};
const STATUSES = ['scheduled', 'live', 'finished'];

export default function ManageMatches() {
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [filterAge, setFilterAge] = useState('all');

  // Form state
  const [homeTeamId, setHomeTeamId] = useState('');
  const [awayTeamId, setAwayTeamId] = useState('');
  const [ageGroup, setAgeGroup] = useState('Junior League');
  const [stage, setStage] = useState('Group Stage');
  const [pitch, setPitch] = useState('1');
  const [matchDate, setMatchDate] = useState('');

  // Edit dialog
  const [editingMatch, setEditingMatch] = useState(null);
  const [editHomeScore, setEditHomeScore] = useState(0);
  const [editPitch, setEditPitch] = useState('');
  const [editAwayScore, setEditAwayScore] = useState(0);
  const [editStatus, setEditStatus] = useState('scheduled');
  const [editScorers, setEditScorers] = useState([]);
  const [editHomeTeamId, setEditHomeTeamId] = useState('');
  const [editAwayTeamId, setEditAwayTeamId] = useState('');
  const [newScorerId, setNewScorerId] = useState('');
  const [newScorerMinute, setNewScorerMinute] = useState('');

  const fetchAll = async () => {
    try {
      const [matchesRes, teamsRes, playersRes] = await Promise.all([
        axios.get(`${API}/matches`),
        axios.get(`${API}/teams`),
        axios.get(`${API}/players`),
      ]);
      setMatches(matchesRes.data);
      setTeams(teamsRes.data);
      setPlayers(playersRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  // Reset stage when league changes
  useEffect(() => {
    const validStages = STAGES_BY_LEAGUE[ageGroup] || ['Group Stage', 'Finals'];
    if (!validStages.includes(stage)) setStage(validStages[0]);
  }, [ageGroup, stage]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (homeTeamId === awayTeamId) {
      toast.error('Home and away teams must be different');
      return;
    }
    try {
      await axios.post(
        `${API}/matches`,
        {
          home_team_id: homeTeamId,
          away_team_id: awayTeamId,
          age_group: ageGroup,
          stage,
          pitch: parseInt(pitch),
          match_date: new Date(matchDate).toISOString(),
          status: 'scheduled',
        },
        { withCredentials: true }
      );
      toast.success('Match created');
      setHomeTeamId('');
      setAwayTeamId('');
      setMatchDate('');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create match');
    }
  };

  const openEdit = (match) => {
    setEditingMatch(match);
    setEditHomeScore(match.home_score);
    setEditAwayScore(match.away_score);
    setEditStatus(match.status);
    setEditScorers(match.scorers || []);
    setEditHomeTeamId(match.home_team_id || '');
    setEditAwayTeamId(match.away_team_id || '');
    setEditPitch(match.pitch ? String(match.pitch) : '');
  };

  const addScorer = () => {
    if (!newScorerId) return;
    const player = players.find(p => p.id === newScorerId);
    if (!player) return;
    setEditScorers([...editScorers, {
      player_id: newScorerId,
      player_name: player.name,
      minute: newScorerMinute ? parseInt(newScorerMinute) : null,
    }]);
    setNewScorerId('');
    setNewScorerMinute('');
  };

  const removeScorer = (idx) => {
    setEditScorers(editScorers.filter((_, i) => i !== idx));
  };

  const handleSaveEdit = async () => {
    if (!editingMatch) return;
    try {
      const payload = {
        home_score: parseInt(editHomeScore),
        away_score: parseInt(editAwayScore),
        status: editStatus,
        scorers: editScorers,
      };
      if (editPitch) payload.pitch = parseInt(editPitch);
      if (editHomeTeamId && editHomeTeamId !== editingMatch.home_team_id) {
        payload.home_team_id = editHomeTeamId;
      }
      if (editAwayTeamId && editAwayTeamId !== editingMatch.away_team_id) {
        payload.away_team_id = editAwayTeamId;
      }
      await axios.patch(
        `${API}/matches/${editingMatch.id}`,
        payload,
        { withCredentials: true }
      );
      toast.success('Match updated');
      setEditingMatch(null);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const handleDelete = async (matchId) => {
    if (!window.confirm('Delete this match?')) return;
    try {
      await axios.delete(`${API}/matches/${matchId}`, { withCredentials: true });
      toast.success('Match deleted');
      fetchAll();
    } catch (error) {
      toast.error('Failed to delete match');
    }
  };

  const teamsInAge = teams.filter(t => t.age_group === ageGroup);
  const teamsInMatchAge = editingMatch ? teams.filter(t => t.age_group === editingMatch.age_group) : [];
  const playersInMatch = editingMatch
    ? players.filter(p => p.team_id === editHomeTeamId || p.team_id === editAwayTeamId)
    : [];
  const stagesForLeague = STAGES_BY_LEAGUE[ageGroup] || ['Group Stage', 'Finals'];

  const filteredMatches = filterAge === 'all' ? matches : matches.filter(m => m.age_group === filterAge);

  return (
    <div className="space-y-6">
      <Card className="bg-[#131C2F] border border-white/10 p-6">
        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Plus className="w-5 h-5 text-[#D4AF37]" />
          Schedule New Match
        </h3>
        <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <Label className="text-white mb-2">Age Group</Label>
            <Select value={ageGroup} onValueChange={setAgeGroup}>
              <SelectTrigger data-testid="match-age-select" className="bg-[#0A0F1C] border-white/20 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AGE_GROUPS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-white mb-2">Stage</Label>
            <Select value={stage} onValueChange={setStage}>
              <SelectTrigger data-testid="match-stage-select" className="bg-[#0A0F1C] border-white/20 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {stagesForLeague.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-white mb-2">Date & Time</Label>
            <Input
              data-testid="match-date-input"
              type="datetime-local"
              value={matchDate}
              onChange={(e) => setMatchDate(e.target.value)}
              required
              className="bg-[#0A0F1C] border-white/20 text-white"
            />
          </div>
          <div>
            <Label className="text-white mb-2">Pitch</Label>
            <Select value={pitch} onValueChange={setPitch}>
              <SelectTrigger data-testid="match-pitch-select" className="bg-[#0A0F1C] border-white/20 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Pitch 1</SelectItem>
                <SelectItem value="2">Pitch 2</SelectItem>
                <SelectItem value="3">Pitch 3</SelectItem>
                <SelectItem value="4">Pitch 4</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-white mb-2">Home Team</Label>
            <Select value={homeTeamId} onValueChange={setHomeTeamId}>
              <SelectTrigger data-testid="match-home-select" className="bg-[#0A0F1C] border-white/20 text-white">
                <SelectValue placeholder="Select home team" />
              </SelectTrigger>
              <SelectContent>
                {teamsInAge.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-white mb-2">Away Team</Label>
            <Select value={awayTeamId} onValueChange={setAwayTeamId}>
              <SelectTrigger data-testid="match-away-select" className="bg-[#0A0F1C] border-white/20 text-white">
                <SelectValue placeholder="Select away team" />
              </SelectTrigger>
              <SelectContent>
                {teamsInAge.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button
              type="submit"
              data-testid="create-match-button"
              className="w-full bg-[#D4AF37] hover:bg-[#F3E5AB] text-[#0A0F1C] font-bold"
            >
              Schedule Match
            </Button>
          </div>
        </form>
      </Card>

      <Card className="bg-[#131C2F] border border-white/10 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-white">All Matches ({filteredMatches.length})</h3>
          <Select value={filterAge} onValueChange={setFilterAge}>
            <SelectTrigger data-testid="filter-matches-age" className="w-40 bg-[#0A0F1C] border-white/20 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Ages</SelectItem>
              {AGE_GROUPS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-3">
          {filteredMatches.map((match) => (
            <div
              key={match.id}
              data-testid={`admin-match-${match.id}`}
              className="bg-[#0A0F1C] border border-white/5 rounded-xl p-4 flex items-center justify-between gap-4"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <Badge className="bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30">{match.stage}</Badge>
                  {match.pitch && (
                    <Badge className="bg-[#0038B8]/20 text-[#0038B8] border-[#0038B8]/40">Pitch {match.pitch}</Badge>
                  )}
                  <Badge className={
                    match.status === 'live' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                    match.status === 'finished' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                    'bg-blue-500/20 text-blue-400 border-blue-500/30'
                  }>
                    {match.status}
                  </Badge>
                  <span className="text-[#9BA3AF] text-xs">{match.age_group}</span>
                </div>
                <p className="text-white font-semibold">
                  {match.home_team.name} <span className="text-[#D4AF37] font-mono mx-2">{match.home_score} - {match.away_score}</span> {match.away_team.name}
                </p>
                <p className="text-[#9BA3AF] text-sm mt-1">{new Date(match.match_date).toLocaleString()}</p>
              </div>
              <div className="flex gap-2">
                <Dialog open={editingMatch?.id === match.id} onOpenChange={(open) => !open && setEditingMatch(null)}>
                  <DialogTrigger asChild>
                    <Button
                      data-testid={`edit-match-${match.id}`}
                      onClick={() => openEdit(match)}
                      size="sm"
                      className="bg-[#0038B8] hover:bg-[#002A8A] text-white"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Update
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#131C2F] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Live Update: {match.home_team.name} vs {match.away_team.name}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-white mb-2 flex items-center gap-2">
                            Home Team
                            {!editHomeTeamId && <span className="text-[10px] text-[#D4AF37] uppercase tracking-wider">Set team</span>}
                          </Label>
                          <Select value={editHomeTeamId} onValueChange={setEditHomeTeamId}>
                            <SelectTrigger data-testid="edit-home-team-select" className={`bg-[#0A0F1C] text-white ${!editHomeTeamId ? 'border-[#D4AF37]/60' : 'border-white/20'}`}>
                              <SelectValue placeholder={match.home_team.name} />
                            </SelectTrigger>
                            <SelectContent>
                              {teamsInMatchAge.map((t) => (
                                <SelectItem key={t.id} value={t.id}>
                                  {t.name}{t.group ? ` (Group ${t.group})` : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-white mb-2 flex items-center gap-2">
                            Away Team
                            {!editAwayTeamId && <span className="text-[10px] text-[#D4AF37] uppercase tracking-wider">Set team</span>}
                          </Label>
                          <Select value={editAwayTeamId} onValueChange={setEditAwayTeamId}>
                            <SelectTrigger data-testid="edit-away-team-select" className={`bg-[#0A0F1C] text-white ${!editAwayTeamId ? 'border-[#D4AF37]/60' : 'border-white/20'}`}>
                              <SelectValue placeholder={match.away_team.name} />
                            </SelectTrigger>
                            <SelectContent>
                              {teamsInMatchAge.map((t) => (
                                <SelectItem key={t.id} value={t.id}>
                                  {t.name}{t.group ? ` (Group ${t.group})` : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4 items-end">
                        <div>
                          <Label className="text-white mb-2">Home Score</Label>
                          <Input
                            data-testid="edit-home-score"
                            type="number"
                            min="0"
                            value={editHomeScore}
                            onChange={(e) => setEditHomeScore(e.target.value)}
                            className="bg-[#0A0F1C] border-white/20 text-white text-3xl text-center font-mono font-bold"
                          />
                        </div>
                        <div className="text-center text-[#9BA3AF] pb-3">VS</div>
                        <div>
                          <Label className="text-white mb-2">Away Score</Label>
                          <Input
                            data-testid="edit-away-score"
                            type="number"
                            min="0"
                            value={editAwayScore}
                            onChange={(e) => setEditAwayScore(e.target.value)}
                            className="bg-[#0A0F1C] border-white/20 text-white text-3xl text-center font-mono font-bold"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-white mb-2">Status</Label>
                          <Select value={editStatus} onValueChange={setEditStatus}>
                            <SelectTrigger data-testid="edit-status-select" className="bg-[#0A0F1C] border-white/20 text-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-white mb-2">Pitch</Label>
                          <Select value={editPitch} onValueChange={setEditPitch}>
                            <SelectTrigger data-testid="edit-pitch-select" className="bg-[#0A0F1C] border-white/20 text-white">
                              <SelectValue placeholder="Select pitch" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1">Pitch 1</SelectItem>
                              <SelectItem value="2">Pitch 2</SelectItem>
                              <SelectItem value="3">Pitch 3</SelectItem>
                              <SelectItem value="4">Pitch 4</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <Label className="text-white mb-2 flex items-center gap-2">
                          <Goal className="w-4 h-4 text-[#D4AF37]" />
                          Goal Scorers
                        </Label>
                        <div className="space-y-2 mb-3">
                          {editScorers.map((scorer, idx) => (
                            <div key={idx} className="flex items-center gap-2 bg-[#0A0F1C] p-2 rounded">
                              <span className="text-white flex-1">
                                {scorer.player_name} {scorer.minute && `(${scorer.minute}')`}
                              </span>
                              <Button
                                onClick={() => removeScorer(idx)}
                                size="sm"
                                className="bg-red-500/20 text-red-400 hover:bg-red-500/30"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                        {(!editHomeTeamId || !editAwayTeamId) && (
                          <div className="mb-3 p-3 rounded-lg bg-[#0038B8]/10 border border-[#0038B8]/30 text-sm text-[#9BA3AF]">
                            <span className="text-[#D4AF37] font-semibold">Tip:</span> Pick the <span className="text-white">Home Team</span> and <span className="text-white">Away Team</span> at the top of this dialog first. Then the scorer dropdown will show all their players.
                          </div>
                        )}
                        <div className="grid grid-cols-3 gap-2">
                          <Select value={newScorerId} onValueChange={setNewScorerId}>
                            <SelectTrigger data-testid="new-scorer-select" className="col-span-2 bg-[#0A0F1C] border-white/20 text-white">
                              <SelectValue placeholder={playersInMatch.length === 0 ? 'Set both teams first' : 'Select scorer'} />
                            </SelectTrigger>
                            <SelectContent>
                              {playersInMatch.length === 0 ? (
                                <div className="px-3 py-2 text-sm text-[#9BA3AF] italic">
                                  No players available. Set Home Team and Away Team first.
                                </div>
                              ) : (
                                playersInMatch.map((p) => (
                                  <SelectItem key={p.id} value={p.id}>{p.name} ({p.team_name})</SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                          <Input
                            data-testid="new-scorer-minute"
                            type="number"
                            placeholder="Min"
                            value={newScorerMinute}
                            onChange={(e) => setNewScorerMinute(e.target.value)}
                            className="bg-[#0A0F1C] border-white/20 text-white"
                          />
                        </div>
                        <Button
                          onClick={addScorer}
                          className="mt-2 w-full bg-[#0038B8] hover:bg-[#002A8A] text-white"
                          data-testid="add-scorer-button"
                        >
                          <Plus className="w-4 h-4 mr-1" /> Add Goal
                        </Button>
                      </div>
                      <Button
                        data-testid="save-match-button"
                        onClick={handleSaveEdit}
                        className="w-full bg-[#D4AF37] hover:bg-[#F3E5AB] text-[#0A0F1C] font-bold"
                      >
                        Save Changes
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
                <Button
                  data-testid={`delete-match-${match.id}`}
                  onClick={() => handleDelete(match.id)}
                  size="sm"
                  className="bg-red-500/20 text-red-400 hover:bg-red-500/30"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
